import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { logger } from '../../services/logging/logging.service';

import { ExternalSettings } from '../../services/external-settings/external-settings.interface';
import { ExternalSettingsService } from '../../services/external-settings/external-settings.service';
import { MfaService } from './mfa.service';
import { ErrorHandler } from '../../error/error.handler';
import { UIUtilityService } from 'src/app/system/utility/ui-utility.service';
import { LocalStorageService, LocalStorageType } from 'src/app/system/utility/local-storage.service';
import { MFAScope } from './mfa-bean';
import { TranslateService } from '@ngx-translate/core';
import { ResponsiveDesignService } from '../../services/responsive-design/responsive-design.service';
import { ResponseCode } from '../../services/api/me-api-response';
import { PushActionPerform, PushNotificationService } from 'src/app/system/utility/push-notification.service';
import { PlatformService } from 'src/app/system/utility/platform.service';

@Component({
    selector: 'app-mfa',
    templateUrl: './mfa.component.html',
    styleUrls: ['./mfa.component.less'],
    standalone: false
})
export class MfaComponent implements OnInit {

  protected PlatformService: any = PlatformService;
  UIUtilityService: any = UIUtilityService;
  mfaForm!: FormGroup;
  externalSettings!: ExternalSettings;
  errors : any = {};
  isSwitchToInternal: boolean = false;
  maxLength: number = 6;
  private _isProcessing : boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MFADialogConfig,
    public dialogRef: MatDialogRef<MfaComponent>,
    public srvMFA: MfaService,
    private srvExternal: ExternalSettingsService,
    private fb: FormBuilder,
    private errorHandler : ErrorHandler,
    private srvLocalStorage: LocalStorageService,
    private translate: TranslateService,
    protected srvRpDesign: ResponsiveDesignService
  ) { }

  ngOnInit(): void {
    forkJoin([
      this.srvExternal.externalSettings$,
    ]).subscribe(([externalSettings]) => {
      this.externalSettings = externalSettings;
      this.isSwitchToInternal = (this.data.status == ResponseCode.MFA_SWITCH_TO_INTERNAL);
      this.maxLength = this.isRealExternalMFA() ? this.maxLength : this.externalSettings.systemVariables.tfaPhraseLength;
      this.initializeForm();
 
      PushNotificationService.getActionPerformNotifications().subscribe((notification) => {
        this.handlePushMessage(notification);
      });

    })
  }

  get isProcessing() : boolean {
    return this._isProcessing;
  }

  isGracePeriodEnable(): boolean {
    let isExternalMfa = this.isRealExternalMFA();
    let isForceMFA = PlatformService.isBrowser() ? this.externalSettings.globalVariables.forceMfaOnTrustedDevices : this.externalSettings.globalVariables.forceMfaOnTrustedMobiBankingDevices;
    let isTFAGracePeriod = this.externalSettings.globalVariables.tfaGracePeriod
    let isLogin = this.data.scope == MFAScope.LOGIN;
    let isRememberMe = this.srvLocalStorage.has(LocalStorageType.REMEMBER_ME);
    return (!isExternalMfa && !isForceMFA && isTFAGracePeriod && isLogin && isRememberMe);
  }

  initializeForm(): void {
    this.mfaForm = this.fb.group({
      mfaCode: ['', [Validators.required, Validators.maxLength(this.maxLength)]],
      trustDevice: []
    });

    this.errorHandler.handleErrors(this.mfaForm, this.errors);
  }

  onConfirm(_event: any): void {
    if (this.mfaForm.invalid) {
      return this.errorHandler.findAllErrors();
    }

    let mfaCode = this.mfaForm.controls['mfaCode'].value;
    let trustDevice = this.mfaForm.controls['trustDevice'].value;

    // Call the service and close the dialog if success
    this._isProcessing = true;
    this.srvMFA.auth(mfaCode, trustDevice).subscribe((res) => {
      this._isProcessing = false;
      this.mfaForm.controls['mfaCode'].setValue("");
      this.mfaForm.controls['mfaCode'].setErrors(null);
    });
  }

  onCancel(_event: any): void {
    this.srvMFA.cancel();
  }
  
  getDialogHeader() : string {
    return this.translate.instant("MFA_VERIFY.DIALOG_HEADER");
  }

  getDialogNote1(): string {
    switch (this.data.scope) {
      case MFAScope.RESET_PASSWORD:
        return this.translate.instant("MFA_VERIFY.FORGOT_PASSWORD_NOTE");
      default :  {
        let key = this.isRealExternalMFA() ? "MFA_VERIFY.EXTERNAL_MFA_NOTE" : "MFA_VERIFY.INTERNAL_MFA_NOTE";
        return this.translate.instant(key);
      }
    }
  }

  getDialogNote2(): string {
    return "";
  }

  private isRealExternalMFA() : boolean {
    let isExternalMfaEnabled = this.externalSettings.globalVariables.isModuleEnabledExternalMfa;
    return (isExternalMfaEnabled && !this.isSwitchToInternal)
  }

  private handlePushMessage(notification: PushActionPerform) {
    logger.debug(`Notification: ${notification}`);

    if (this.srvMFA.dialogRef.getState() == MatDialogState.OPEN) {
      let data = notification.data
      let body = data.body;
      let title = data.title;

      if (!title || !body) return;

      // // Prefill MFA Token
      // if (body.startsWith("Your security code is ")) {
      //   let mfaCode = body.substring(22);
      //   logger.debug(`Prefill MFA Code: ${mfaCode}`);
      //   this.mfaForm.controls['mfaCode'].patchValue(mfaCode);
      // };
    }
  }
}
  
export interface MFADialogConfig {
  scope: MFAScope;
  status: ResponseCode;
}